import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { usePets } from '../../../context/PetsContext';
import { useTutorial } from '../../../context/TutorialContext';
import { useUser } from '../../../context/UserContext';
import { useToast } from '../../../hooks/UI/useToast';
import { transformUser } from '../../../types/User/types';
import { isDawerPet } from '../../../utils/coverage';
import { isDawerProfileCompletionComplete } from '../../../utils/user';
import Modal from '../../UI/Modal';
import Toast from '../../UI/Toast';
import UserForm from '../UserForm';

const ProfileCompletionGate: React.FC = () => {
  const { user, isValidating, mutate } = useUser();
  const { pets, isValidating: isPetsValidating } = usePets();
  const { startTour } = useTutorial();
  const { toastState, showToast, hideToast } = useToast();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [hasPromptedThisSession, setHasPromptedThisSession] = useState(false);

  const { userData, addressData } = useMemo(() => transformUser(user), [user]);
  const petList = useMemo(() => (Array.isArray(pets) ? pets : []), [pets]);
  const hasOnlyDawerPets =
    petList.length > 0 &&
    petList.some((pet) => isDawerPet(pet)) &&
    petList.every((pet) => isDawerPet(pet));
  const hasIncompleteDawerProfile = !isDawerProfileCompletionComplete(user);
  const shouldRequestProfileCompletion =
    hasOnlyDawerPets && hasIncompleteDawerProfile;
  const canPromptWhileTourClosed = !startTour;

  useEffect(() => {
    if (!hasIncompleteDawerProfile) {
      hideToast();
      setHasPromptedThisSession(false);
    }
  }, [hasIncompleteDawerProfile, hideToast]);

  const openForm = useCallback(() => {
    hideToast();
    setIsFormOpen(true);
  }, [hideToast]);

  useEffect(() => {
    if (
      !canPromptWhileTourClosed ||
      isValidating ||
      isPetsValidating ||
      isFormOpen ||
      toastState.isOpen ||
      hasPromptedThisSession ||
      !shouldRequestProfileCompletion
    ) {
      return;
    }

    const timeout = window.setTimeout(() => {
      setHasPromptedThisSession(true);
      showToast({
        message: 'Faltan datos por cargar en tu perfil.',
        color: 'warning',
        duration: 0,
        actionText: 'Completar',
        onAction: openForm,
      });
    }, 1000);

    return () => window.clearTimeout(timeout);
  }, [
    hasPromptedThisSession,
    canPromptWhileTourClosed,
    isFormOpen,
    isPetsValidating,
    isValidating,
    openForm,
    shouldRequestProfileCompletion,
    showToast,
    toastState.isOpen,
  ]);

  const handleComplete = useCallback(async () => {
    setIsFormOpen(false);
    hideToast();
    await mutate();
  }, [hideToast, mutate]);

  return (
    <>
      <Toast
        isOpen={toastState.isOpen}
        message={toastState.message}
        color={toastState.color}
        duration={toastState.duration}
        actionText={toastState.actionText}
        onAction={toastState.onAction}
        onDidDismiss={hideToast}
      />

      <Modal
        isOpen={isFormOpen}
        navigation={false}
        dismissible
        onClose={() => setIsFormOpen(false)}
      >
        <UserForm
          userData={userData}
          addressData={addressData}
          closeModal={handleComplete}
          submitMode="direct"
        />
      </Modal>
    </>
  );
};

export default React.memo(ProfileCompletionGate);
